import {
  ELayoutPosition,
  ESdChangeReason,
  IDisplayItem,
  IDisplayItemProperty,
  IScreenDesign,
  IScreenDesignDictionary
} from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";

/**
 * Safely parse a value that may be a JSON string.
 * Returns the parsed value if valid JSON, otherwise returns the original value.
 */
export function safeParse(value: unknown): unknown {
  if (value == null) return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

/**
 * Alias for safeParse — used in modules that historically named it safeParseJsonish.
 */
export const safeParseJsonish = safeParse;

/**
 * Parse a value that may be an option path (array of numeric indices).
 * Returns a normalized number[] or null.
 */
export function parseOptionPath(value: unknown): number[] | null {
  const parsed = safeParse(value);
  if (Array.isArray(parsed)) {
    const normalized = parsed.map((item) => Number(item)).filter((item) => Number.isInteger(item) && item >= 0);
    return normalized.length === parsed.length ? normalized : null;
  }

  if (typeof value === "string" && /^[\d\s,.[\]]+$/.test(value)) {
    const normalized = value
      .split(/[,\s.[\]]+/)
      .filter(Boolean)
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item >= 0);
    return normalized.length > 0 ? normalized : null;
  }

  return null;
}

/** Get the screen design dictionary from the SD context. */
export function getScreenDictionary(sd: IScreenDesignContextState): IScreenDesignDictionary {
  return sd.screenDesignDictionary || {};
}

/** Get the current screen design object from the SD context. */
export function getScreenDesign(sd: IScreenDesignContextState): IScreenDesign {
  return sd.getScreenDesign();
}

/** Set the screen design on the SD context. */
export function setScreenDesign(
  sd: IScreenDesignContextState,
  screenDesign: IScreenDesign,
  changeReason: ESdChangeReason,
  kuikaId?: string
): void {
  sd.setScreenDesign(screenDesign, changeReason, kuikaId);
}

/** Get the screen provider — returns the SD state itself (which contains screen provider methods). */
export function getScreenProvider(sd: IScreenDesignContextState): IScreenDesignContextState {
  return sd;
}

/** Lookup a display item by kuikaId from the screen dictionary. */
export function getDisplayItem(sd: IScreenDesignContextState, kuikaId: string | number): IDisplayItem | undefined {
  const dict = getScreenDictionary(sd);
  return dict[typeof kuikaId === "string" ? parseInt(kuikaId, 10) : kuikaId];
}

/**
 * Set a layout region's `layoutPosition` property to `status`, ADDING the
 * property when the region lacks it.
 *
 * KOA-17777 — the MasterLayout template declares `layoutPosition` ONLY on the
 * RIGHT region (-13). Header/footer/left don't have it, so the old
 * `setDiLayoutPosition` (which only wrote an EXISTING property) was a silent
 * no-op for them — an unused region (e.g. header/footer in a left-nav shell)
 * could never be hidden, by the layout-settings UI OR the AI master-shell, both
 * of which call `setLayoutPosition`. When the property is absent we clone
 * `templateProp` (the right region's property — the one guaranteed to exist) so
 * the synthesized property matches the platform's exact shape, then write the
 * requested position. This adds the property ONLY when a position is explicitly
 * set, so it never changes the default rendering of a region nobody touched.
 *
 * Degrades to a no-op (never throws) when the region or template property is
 * unavailable — same fail-soft posture as the original.
 */
export function ensureLayoutPositionProperty(
  di: IDisplayItem | undefined,
  status: ELayoutPosition,
  templateProp: IDisplayItemProperty | undefined
): void {
  const props: IDisplayItemProperty[] | undefined = di?.item?.properties;
  if (!props) return;
  const existing: IDisplayItemProperty | undefined = props.find((p) => p.name === "layoutPosition");
  if (existing) {
    existing.symbol.fixedValue = status;
    return;
  }
  if (!templateProp) return;
  const cloned: IDisplayItemProperty = JSON.parse(JSON.stringify(templateProp));
  cloned.symbol.fixedValue = status;
  props.push(cloned);
}
